import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type GuestType = 'weekend' | 'farmhouse' | null;

interface GuestContextType {
  guestType: GuestType;
  setGuestType: (type: GuestType) => void;
  isAuthenticated: boolean;
}

const GuestContext = createContext<GuestContextType | undefined>(undefined);

export const useGuest = () => {
  const context = useContext(GuestContext);
  if (!context) {
    throw new Error('useGuest must be used within a GuestProvider');
  }
  return context;
};

interface GuestProviderProps {
  children: ReactNode;
}

export const GuestProvider: React.FC<GuestProviderProps> = ({ children }) => {
  const [guestType, setGuestTypeState] = useState<GuestType>(null);

  // Load guest type from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('guestType');
    if (stored === 'weekend' || stored === 'farmhouse') {
      setGuestTypeState(stored);
    }
  }, []);

  // Persist to localStorage whenever it changes
  const setGuestType = (type: GuestType) => {
    if (type) {
      localStorage.setItem('guestType', type);
    } else {
      localStorage.removeItem('guestType');
    }
    setGuestTypeState(type);
  };

  const isAuthenticated = guestType !== null;

  return (
    <GuestContext.Provider value={{ guestType, setGuestType, isAuthenticated }}>
      {children}
    </GuestContext.Provider>
  );
};
